import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  createClientApiCredential,
  deleteClientApiCredential,
  getClientApiCredentials,
  setClientApiCredentialPassword,
} from "../services/superAdminApi";

export default function ClientAccessManagement() {
  const [label, setLabel] = useState("");
  const [credentials, setCredentials] = useState(null);
  const [creating, setCreating] = useState(false);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientPasswords, setClientPasswords] = useState({});
  const [displayedPasswords, setDisplayedPasswords] = useState({});
  const [savingPassword, setSavingPassword] = useState("");
  const [deletingClient, setDeletingClient] = useState("");

  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const { data } = await getClientApiCredentials();
      setClients(data.clients);
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not load client accounts.");
    } finally {
      setLoadingClients(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const createCredentials = async (event) => {
    event.preventDefault();
    setCreating(true);
    setCredentials(null);
    try {
      const { data } = await createClientApiCredential(label.trim());
      setCredentials(data.credentials);
      setDisplayedPasswords((current) => ({
        ...current,
        [data.credentials.clientId]: data.credentials.clientPassword,
      }));
      setLabel("");
      await loadClients();
      toast.success(data.message || "Client login generated.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not generate client login.");
    } finally {
      setCreating(false);
    }
  };

  const copyCredentials = async () => {
    if (!credentials) return;
    try {
      await navigator.clipboard.writeText(
        `Client ID: ${credentials.clientId}\nClient Password: ${credentials.clientPassword}`,
      );
      toast.success("Client ID and password copied.");
    } catch (error) {
      toast.error("Clipboard access was blocked. Copy the credentials from the fields below.");
    }
  };

  const changePassword = async (event, targetClientId) => {
    event.preventDefault();
    const password = clientPasswords[targetClientId] || "";
    setSavingPassword(targetClientId);
    try {
      const { data } = await setClientApiCredentialPassword(targetClientId, password);
      setDisplayedPasswords((current) => ({ ...current, [targetClientId]: password }));
      toast.success(data.message || "Client password changed.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not change client password.");
    } finally {
      setSavingPassword("");
    }
  };

  const copyClientCredentials = async (targetClientId) => {
    const password = displayedPasswords[targetClientId];
    if (!password) {
      toast.error("Set a new password to display it; saved passwords cannot be recovered.");
      return;
    }
    try {
      await navigator.clipboard.writeText(`Client ID: ${targetClientId}\nClient Password: ${password}`);
      toast.success("Client ID and password copied.");
    } catch (error) {
      toast.error("Clipboard access was blocked. Copy the credentials displayed on the page.");
    }
  };

  const removeClient = async (targetClientId) => {
    if (!window.confirm(`Delete client account ${targetClientId}? This cannot be undone.`)) return;
    setDeletingClient(targetClientId);
    try {
      const { data } = await deleteClientApiCredential(targetClientId);
      setClients((current) => current.filter((client) => client.clientId !== targetClientId));
      setDisplayedPasswords((current) => {
        const updated = { ...current };
        delete updated[targetClientId];
        return updated;
      });
      setClientPasswords((current) => {
        const updated = { ...current };
        delete updated[targetClientId];
        return updated;
      });
      toast.success(data.message || "Client account deleted.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not delete client account.");
    } finally {
      setDeletingClient("");
    }
  };

  return (
    <main className="mx-auto max-w-3xl space-y-6">
      <header>
        <h1 className="mt-1 text-2xl font-black text-[#640101]">Generate client login</h1>
        <p className="mt-2 text-sm text-gray-600">
          Generate credentials for the separate Client Admin panel. The password is shown only once.
        </p>
      </header>

      <section className="kanchira-card">
        <form onSubmit={createCredentials} className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Client name
            <input
              className="kanchira-input mt-1"
              required
              maxLength={100}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="For example, Sai Kiran"
            />
          </label>
          <button type="submit" className="btn-primary" disabled={creating}>
            {creating ? "Generating credentials..." : "Generate Client ID and Password"}
          </button>
        </form>
      </section>

      <section className="kanchira-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-[#640101]">Generated client accounts</h2>
            <p className="mt-1 text-sm text-gray-600">
              Generated and newly set passwords are displayed here until you leave or refresh this page. Older saved passwords cannot be recovered; set a new one to share it.
            </p>
          </div>
          <button type="button" className="btn-gold" onClick={loadClients} disabled={loadingClients}>
            {loadingClients ? "Loading..." : "Refresh list"}
          </button>
        </div>
        {loadingClients ? (
          <p className="mt-4 text-sm text-gray-600">Loading client accounts...</p>
        ) : clients.length === 0 ? (
          <p className="mt-4 text-sm text-gray-600">No client accounts have been generated.</p>
        ) : (
          <ul className="mt-4 divide-y divide-gray-200">
            {clients.map((client) => (
              <li key={client.clientId} className="space-y-3 py-4 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-mono font-semibold text-gray-900">{client.clientId}</p>
                    {client.label && <p className="text-sm text-gray-600">{client.label}</p>}
                    <p className="text-xs text-gray-500">
                      Created {new Date(client.createdAt).toLocaleString()}
                      {!client.isActive && " · Disabled"}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="rounded-lg border border-red-300 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                    onClick={() => removeClient(client.clientId)}
                    disabled={deletingClient === client.clientId}
                  >
                    {deletingClient === client.clientId ? "Deleting..." : "Delete client"}
                  </button>
                </div>
                <form onSubmit={(event) => changePassword(event, client.clientId)} className="flex flex-col gap-2 sm:flex-row">
                  <label className="block flex-1 text-sm font-medium text-gray-700">
                    Password (8–72 UTF-8 bytes)
                    <input
                      type="text"
                      className="kanchira-input mt-1"
                      required
                      minLength={8}
                      maxLength={72}
                      autoComplete="off"
                      value={clientPasswords[client.clientId] || ""}
                      onChange={(event) => setClientPasswords((current) => ({
                        ...current,
                        [client.clientId]: event.target.value,
                      }))}
                    />
                  </label>
                  <button
                    type="submit"
                    className="btn-primary self-end"
                    disabled={savingPassword === client.clientId}
                  >
                    {savingPassword === client.clientId ? "Saving..." : "Set password"}
                  </button>
                </form>
                {displayedPasswords[client.clientId] ? (
                  <div className="rounded-lg bg-amber-50 p-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Current client password
                      <input
                        className="kanchira-input mt-1 bg-white font-mono"
                        readOnly
                        value={displayedPasswords[client.clientId]}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn-gold mt-2"
                      onClick={() => copyClientCredentials(client.clientId)}
                    >
                      Copy client ID and password
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {credentials && (
        <section className="rounded-xl border border-amber-300 bg-amber-50 p-5 shadow-sm" aria-live="polite">
          <h2 className="text-lg font-bold text-amber-950">Save these credentials now</h2>
          <p className="mt-1 text-sm text-amber-900">
            The password is stored as a hash and cannot be viewed again after leaving this page.
          </p>
          <div className="mt-4 space-y-3">
            <label className="block text-sm font-semibold text-gray-700">
              Client ID
              <input className="kanchira-input mt-1 bg-white font-mono" readOnly value={credentials.clientId} />
            </label>
            <label className="block text-sm font-semibold text-gray-700">
              Client Password
              <input className="kanchira-input mt-1 bg-white font-mono" readOnly value={credentials.clientPassword} />
            </label>
          </div>
          <button type="button" className="btn-gold mt-4" onClick={copyCredentials}>
            Copy credentials
          </button>
          <p className="mt-4 text-sm text-gray-700">
            Give these to the client to sign in at <code className="rounded bg-white px-1 py-0.5">/client-admin/login</code>.
            The client login creates a bearer token in that browser’s local storage for Product ID invoice lookup.
          </p>
        </section>
      )}
    </main>
  );
}
